<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0" class="glass-panel">
    <VaAccordion v-model="value" multiple >
      <VaCollapse 
        v-for="(route, index) in navigationRoutes.routes" 
        :key="index" 
        :model-value="value[index]" 
        @update:model-value="(val) => onToggleSection(val, index)"
      >
        <template #header>
          <SidebarItemRecursive 
            :route="route" 
            :depth="0"
            :active-color="activeColor"
            :is-open="value[index]"
            @toggle="(val) => onToggleSection(val, index)"
          />
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import SidebarItemRecursive from './SidebarItemRecursive.vue'

export default defineComponent({
  name: 'Sidebar',
  components: {
    SidebarItemRecursive
  },
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],
  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const onToggleSection = (val: boolean, index: number) => {
      value.value[index] = val
    }

    const expandActiveSections = () => {
      value.value = navigationRoutes.routes.map(route => {
        return hasActiveChild(route)
      })
    }

    const hasActiveChild = (route: INavigationRoute): boolean => {
      if (!route.children) return route.name === route.name
      return route.children.some(child => {
        return child.name === route.name || hasActiveChild(child)
      })
    }

    onMounted(() => {
      expandActiveSections()
      setTimeout(expandActiveSections, 100)
    })

    watch(() => route.fullPath, expandActiveSections)

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      onToggleSection,
      t
    }
  },
})
</script>